import { onBeforeUnmount, onMounted, type Ref } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

function motionAllowed(): boolean {
  return (
    typeof window !== 'undefined' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/** Fade-up reveal for `[data-reveal]` descendants of `root`. */
export function useReveal(root: Ref<HTMLElement | null>) {
  let ctx: gsap.Context | null = null

  onMounted(() => {
    if (!root.value || !motionAllowed()) return
    ctx = gsap.context(() => {
      const targets = gsap.utils.toArray<HTMLElement>('[data-reveal]')
      for (const el of targets) {
        gsap.fromTo(
          el,
          { y: 28, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%' },
          },
        )
      }
      const staggers = gsap.utils.toArray<HTMLElement>('[data-reveal-group]')
      for (const group of staggers) {
        const items = group.querySelectorAll('[data-reveal-item]')
        if (items.length === 0) continue
        gsap.fromTo(
          items,
          { y: 24, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.55,
            ease: 'power3.out',
            stagger: 0.08,
            scrollTrigger: { trigger: group, start: 'top 85%' },
          },
        )
      }
    }, root.value)
  })

  onBeforeUnmount(() => {
    ctx?.revert()
  })
}

/** Animated integer counter driven by ScrollTrigger. */
export function useCounter(
  el: Ref<HTMLElement | null>,
  target: number,
  render: (n: number) => void,
) {
  onMounted(() => {
    if (!el.value) return
    if (!motionAllowed()) {
      render(target)
      return
    }
    const state = { n: 0 }
    const tween = gsap.to(state, {
      n: target,
      duration: 1.4,
      ease: 'power2.out',
      scrollTrigger: { trigger: el.value, start: 'top 90%' },
      onUpdate: () => render(Math.round(state.n)),
    })
    onBeforeUnmount(() => {
      tween.scrollTrigger?.kill()
      tween.kill()
    })
  })
}
